import { WORKSPACE_PLAN } from '@/entities/workspace/constants';
import { TALK_TO_SALES_LINK } from '@/features/setting/helpers/talk-to-sales';
import { trackEvent } from '@/shared/track';

export interface ConstraintActionLink {
  kind: 'internal' | 'external';
  label: string;
  href: string;
}

export interface ConstraintToastContent {
  title: string;
  description: string;
  action: ConstraintActionLink | null;
}

export interface SavedListUsageState {
  currentCount: number;
  limit: number;
  remaining: number;
  isNearLimit: boolean;
  isAtLimit: boolean;
}

export interface OpenConstraintActionOptions {
  navigateInternal?: (href: string) => void;
}

export function getSavedListUsageState(currentCount: number, limit: number): SavedListUsageState {
  const normalizedLimit = Math.max(limit, 0);
  const remaining = Math.max(normalizedLimit - currentCount, 0);

  return {
    currentCount,
    limit: normalizedLimit,
    remaining,
    isNearLimit: remaining <= 1,
    isAtLimit: currentCount >= normalizedLimit,
  };
}

export function getSavedListLimitDescription(usage: SavedListUsageState): string {
  if (usage.isAtLimit) {
    return 'You have reached your workspace limit for saved lists';
  }

  if (usage.isNearLimit) {
    return `${usage.remaining} saved list remaining before you reach the limit`;
  }

  return `${usage.remaining} saved lists remaining in this workspace`;
}

export function getPrimaryConstraintAction(
  plan: WORKSPACE_PLAN,
  workspaceId: string,
): ConstraintActionLink {
  if (plan === WORKSPACE_PLAN.TRIAL) {
    trackEvent('Plan', 'upgrade_lead', {
      to: 'basic',
    });
    return {
      kind: 'internal',
      label: 'Upgrade to Basic',
      href: `/workspace/${workspaceId}/settings/plan`,
    };
  }

  trackEvent('Plan', 'upgrade_lead', {
    to: 'enterprise',
  });

  return {
    kind: 'external',
    label: 'Talk to sales',
    href: TALK_TO_SALES_LINK,
  };
}

export function openConstraintAction(
  action: ConstraintActionLink,
  options?: OpenConstraintActionOptions,
): void {
  if (action.kind === 'internal') {
    if (options?.navigateInternal) {
      options.navigateInternal(action.href);
      return;
    }

    if (typeof window === 'undefined') return;
    window.location.assign(action.href);
    return;
  }

  if (typeof window === 'undefined') return;
  window.open(action.href, '_blank', 'noopener,noreferrer');
}

export function getSavedListCapacityDialogContent(
  plan: WORKSPACE_PLAN,
  workspaceId: string,
): ConstraintToastContent {
  const action = getPrimaryConstraintAction(plan, workspaceId);

  if (plan === WORKSPACE_PLAN.TRIAL) {
    return {
      title: 'You have reached your saved list limit',
      description: 'Delete an existing list or upgrade to Basic to create more saved lists.',
      action,
    };
  }

  return {
    title: 'You have reached your saved list limit',
    description: 'Delete an existing list or talk to sales if you need a higher limit.',
    action,
  };
}

export function getMonthlyExportCapacityDialogContent(
  plan: WORKSPACE_PLAN,
  workspaceId: string,
): ConstraintToastContent {
  const action = getPrimaryConstraintAction(plan, workspaceId);

  if (plan === WORKSPACE_PLAN.TRIAL) {
    return {
      title: 'Monthly export limit exceeded',
      description:
        'Try exporting fewer creators, or upgrade to Basic to unlock more export capacity.',
      action,
    };
  }

  return {
    title: 'Monthly export limit exceeded',
    description: 'Try exporting fewer creators, or talk to sales if you need a higher limit.',
    action,
  };
}
